import { entries } from '@rehaje/shared'

import { SchemaElementDefinition } from './SchemaDefinition'
import { ElementDefinition } from './TypedDefinition'
import { ElementNode } from './TypedNode'
import { createElementComponent } from './ViewComponent'
import { ElementModel } from './ViewModel'

import type { ActionNodeConstructor } from '../Action/plugin'
import type { ElementSpecific } from './TypedNode'
import type {
  PluginMiddlewares,
  PluginVisitors,
  RehajePlugin,
  TypedDefinitionConstructor,
  TypedNodeConstructor,
  ViewModelConstructor
} from '@rehaje/core'
import type { ViewComponent } from '@rehaje/react'
import type { TRecord } from '@rehaje/shared'
import type { FC } from 'react'

export type EC<T extends TRecord = {}> = ElementComponent & FC<T>

export interface ElementComponent {
  /**
   * 组件动作集合
   */
  actions?: Record<string, ActionNodeConstructor>
  /**
   * 组件类型定义，必须继承 ElementDefinition
   */
  definition?: ElementDefinitionConstructor
  /**
   * 组件视图模型，必须继承 ElementModel
   */
  model?: ElementModelConstructor

  /**
   * 组件 TypedNode
   */
  node?: ElementNodeConstructor
}

export type ElementComponents<T> = Record<string, ElementComponent & T>

export interface ElementDefinitionConstructor {
  new (): ElementDefinition
  prototype: ElementDefinition
}

export interface ElementModelConstructor {
  new (node: ElementNode): ElementModel
  prototype: ElementModel
}

export interface ElementNodeConstructor {
  new (specific: ElementSpecific): ElementNode
  prototype: ElementNode
}
export interface ElementPluginProps<T extends boolean = true> {
  /**
   * 自动创建组件连接器组件，如果没有传 getViewComponent 进行连接，配置为 true 会默认连接
   * 默认为 true
   */
  autoConnect?: boolean
  /**
   * 组件集合
   */
  components?: ElementComponents<
    T extends false ? ViewComponent<ElementNode, ElementModel> : FC<any>
  >
  /**
   * 兜底组件
   */
  fallback?: ElementComponent &
    (T extends false ? ViewComponent<ElementNode, ElementModel> : FC<any>)
  /**
   * 类型定义包装器，可提供 HOC 能力
   * @example
   * new ElementPlugin({
   *   components:{...},
   *   getTypedDefinition(ComponentTypedDefinition){
   *     return class extends ComponentTypedDefinition {
   *      //...
   *     }
   *   }
   * })
   * @param definition
   * @returns
   */
  getTypedDefinition?: (
    definition?: TypedDefinitionConstructor
  ) => TypedDefinitionConstructor
  /**
   * TypedNode 包装器，可提供 HOC 能力
   * @example
   * new ElementPlugin({
   *   components:{...},
   *   getTypedNode(ComponentTypedNode){
   *     return class extends ComponentTypedNode {
   *      //...
   *     }
   *   }
   * })
   * @param node
   * @returns
   */
  getTypedNode?: (node?: TypedNodeConstructor) => TypedNodeConstructor
  /**
   * 组件包装器，适用所有组件，可以提供 HOC 能力
   * 注意：默认组件包装器对 props 是浅复制依赖收集
   * @example
   * new ElementPlugin({
   *   components:{...},
   *   getViewComponent(Origin){
   *     return ({model,node})=>{
   *       return <Origin {...model.props}/>
   *     }
   *   }
   * })
   * @param component
   * @returns
   */
  getViewComponent?: (
    component?: T extends true ? ViewComponent : FC<TRecord>
  ) => ViewComponent<ElementNode, ElementModel>
  /**
   * 视图模型包装器，可提供 HOC 能力
   * @example
   * new ElementPlugin({
   *   components:{...},
   *   getViewModel(ComponentViewModel){
   *     return class extends ComponentViewModel {
   *      //...
   *     }
   *   }
   * })
   * @param model
   * @returns
   */
  getViewModel?: (model?: ViewModelConstructor) => ViewModelConstructor
}
/**
 * 组件插件
 * 能力：
 * 1. 提供组件批量注册能力
 * 2. 统一组件注册 API
 * 3. 提供 Fallback 机制
 */
export class ElementPlugin implements RehajePlugin {
  middlewares: PluginMiddlewares
  props: ElementPluginProps
  visitors: PluginVisitors

  constructor(props?: ElementPluginProps<false> & { autoConnect: false })
  constructor(props?: ElementPluginProps<true>)
  constructor(props?: any) {
    this.props = {
      autoConnect: true,
      ...props
    } as ElementPluginProps
    this.visitors = this.__r_createVisitors()
    this.middlewares = this.__r_createMiddlewares()
  }

  __r_createMiddlewares(): PluginMiddlewares {
    return {
      createTypedNode: [this.__r_getFallbackMiddleware()]
    }
  }

  __r_createVisitors() {
    const components = this.props.components ?? {}
    const visitors: PluginVisitors = {
      definitions: {
        Element: ElementDefinition,
        'Schema.Element': SchemaElementDefinition
      }
    }
    for (const [key, component] of entries(components)) {
      const type = `Element.${key}`
      visitors.definitions ??= {}
      visitors.components ??= {}
      visitors.models ??= {}
      visitors.nodes ??= {}
      visitors.components[type] = this.__r_getViewComponent(component)
      visitors.models[type] = this.__r_getViewModel(
        component.model ?? ElementModel
      )
      visitors.nodes[type] = this.__r_getTypedNode(component.node ?? ElementNode)
      visitors.definitions[type] = this.__r_getTypedDefinition(
        component.definition ??
          class extends ElementDefinition {
            override extends = ['Element']
          }
      )
    }
    return visitors
  }

  __r_getFallbackMiddleware(): RehajeMiddlewares['createTypedNode'] {
    const fallback = this.props.fallback
    const getProps = () => this.props
    return (ctx, next) => {
      const type = ctx.payload?.type as string
      if (
        type?.startsWith('Element.') &&
        !ctx.store.plugin.hasTypedNodeClass(type)
      ) {
        ctx.store.plugin.addPlugin({
          get props(): ElementPluginProps {
            return getProps()
          },
          visitors: {
            components: {
              [type]: this.__r_getViewComponent(fallback as any)
            },
            definitions: {
              [type]: this.__r_getTypedDefinition(
                fallback?.definition ?? ElementDefinition
              )
            },
            models: {
              [type]: this.__r_getViewModel(fallback?.model ?? ElementModel)
            },
            nodes: {
              [type]: this.__r_getTypedNode(fallback?.node ?? ElementNode)!
            }
          }
        })
      }
      return next(ctx)
    }
  }

  __r_getTypedDefinition(definition?: TypedDefinitionConstructor) {
    if (this.props.getTypedDefinition)
      return this.props.getTypedDefinition(definition)
    return definition
  }

  __r_getTypedNode(node?: TypedNodeConstructor) {
    if (this.props.getTypedNode) return this.props.getTypedNode(node)
    return node
  }

  __r_getViewComponent(component?: FC): ViewComponent<ElementNode, ElementModel> {
    if (component) {
      if (this.props.getViewComponent)
        return this.props.getViewComponent(component)
      return this.props.autoConnect === false
        ? component
        : createElementComponent(component)
    }
    return createElementComponent()
  }

  __r_getViewModel(model: ViewModelConstructor) {
    if (this.props.getViewModel) return this.props.getViewModel(model)
    return model
  }
}
